import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
// Remove Database type import if no longer needed for direct DB calls
// import type { Database } from '@/integrations/supabase/types'
import type {
	WidgetPreviewProps,
	WidgetConfig,
} from '@/components/widget-builder/WidgetPreview'
import { WidgetPreview } from '@/components/widget-builder/WidgetPreview'
import { createRoot } from 'react-dom/client'
import { parseUserAgent } from '@/utils/userAgent'
import { toast } from '@/components/ui/use-toast'
import { getWidgetDetails, ApiWidget } from '@/lib/api' // Import API function and type

// Remove old type definitions based on Database schema
// type CustomQuestionRow = Database['public']['Tables']['custom_questions']['Row']
// type WidgetRow = Database['public']['Tables']['widgets']['Row']
// type WidgetWithQuestions = WidgetRow & {
// 	custom_questions: CustomQuestionRow[]
// }

interface EmbedWidgetProps {
	widgetId: string
	onRemove?: () => void
	showRemoveButton?: boolean
	initialWidgetData?: ApiWidget // Expect ApiWidget type now
}

export function EmbedWidgetContainer({
	widgetId,
	onRemove,
	showRemoveButton = false,
	initialWidgetData,
}: EmbedWidgetProps) {
	// Update useQuery to fetch using the API function and expect ApiWidget type
	const { data: widget, isLoading } = useQuery<ApiWidget, Error>({
		queryKey: ['widget', widgetId],
		queryFn: () => getWidgetDetails(widgetId), // Use API function
		initialData: initialWidgetData,
		enabled: !initialWidgetData, // Only fetch if initial data is NOT provided
	})

	useEffect(() => {
		if (isLoading || !widget) return

		const container = document.createElement('div')
		// Setup container styles based on ApiWidget fields
		const position = widget.position ?? 'bottom-right' // Use widget fields directly
		container.style.position = 'fixed'
		container.style.bottom = position.includes('bottom') ? '20px' : 'auto'
		container.style.top = position.includes('top') ? '20px' : 'auto'
		container.style.right = position.includes('right') ? '20px' : 'auto'
		container.style.left = position.includes('left') ? '20px' : 'auto'
		container.style.zIndex = '9999'
		container.style.width = '350px'

		document.body.appendChild(container)
		const root = createRoot(container)

		// --- Map ApiWidget data to WidgetConfig structure ---
		// The mapping logic remains largely the same, just sourcing from ApiWidget type
		const themeConfig = widget.theme_config || {}

		const previewConfig: WidgetConfig = {
			id: widget.id,
			team_id: widget.team_id,
			name: widget.name,
			program_id: widget.program_id,
			header_title: widget.header_title ?? 'Provide Feedback',
			position: position as WidgetConfig['position'], // Use calculated position
			primaryColor: widget.primary_color ?? '#000000',
			gradientEndColor: widget.gradient_end_color,
			backgroundColor: widget.background_color ?? '#FFFFFF',
			textColor: widget.text_color ?? '#000000',
			enable_survey: widget.enable_survey ?? true,
			enable_bug_reporting: widget.enable_bug_reporting ?? false,
			enable_contact_messages: widget.enable_contact_messages ?? false,
			enable_feature_requests: widget.enable_feature_requests ?? false,
			enable_reviews: widget.enable_reviews ?? false,
			enable_live_chat: widget.enable_live_chat ?? false,
			primary_feedback_type: (widget.primary_feedback_type ??
				widget.type) as WidgetConfig['primary_feedback_type'], // Use type if primary is missing
			question: widget.question,
			customQuestions: (widget.custom_questions || []).map((q) => ({
				id: q.id,
				type: q.type as WidgetConfig['customQuestions'][number]['type'],
				question: q.question,
				required: q.required ?? false,
				options: q.options ?? undefined,
				order_index: q.order_index ?? 0,
				min_scale: q.min_scale ?? undefined,
				max_scale: q.max_scale ?? undefined,
			})),
			buttonText: widget.button_text,
			thankYou: widget.thank_you ?? {
				title: 'Thank you!',
				message: 'Feedback submitted.',
			},
			feedbackButton: {
				show: widget.show_feedback_button ?? true,
				text: widget.feedback_button_text ?? 'Feedback',
				color: widget.feedback_button_color ?? '#000000',
				textColor: widget.feedback_button_text_color ?? '#FFFFFF',
			},
			showBranding: widget.show_branding ?? true,
			// Map theme-specific fields from JSONB
			brandingStyle: themeConfig.brandingStyle || {},
			menuItemColors: themeConfig.menuItemColors || {},
			menuItemStyle: themeConfig.menuItemStyle || { layout: 'default' },
			animation: themeConfig.animation || { header: 'none' },
			glassmorphism: themeConfig.glassmorphism || { enabled: false },
			layout: themeConfig.layout || {
				spacing: 'default',
				containerBorder: true,
			},
		}

		root.render(<WidgetPreview config={previewConfig} />)

		return () => {
			// Schedule unmount asynchronously
			setTimeout(() => {
				try {
					root.unmount()
				} catch (error) {
					console.warn('Error during widget root unmount:', error)
				}
				if (container.parentNode) {
					container.parentNode.removeChild(container)
				}
			}, 0) // Execute shortly after current event loop cycle
		}
	}, [widgetId, onRemove, showRemoveButton, widget, isLoading])

	return null
}
